import type { TokenPair } from '@/api/types'

/**
 * HTTP-клиент. Токены он не хранит — их подставляют через `configureClient`.
 *
 * Инверсия сделана затем, чтобы разорвать цикл «клиент знает про авторизацию, авторизация ходит
 * клиентом»: провайдер отдаёт сюда геттеры, клиент читает актуальные значения замыканием и не
 * ловит устаревшие.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    readonly detail: string,
  ) {
    super(detail)
    this.name = 'ApiError'
  }
}

interface ConfigureOptions {
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
  onTokenRefreshed: (pair: TokenPair) => void
  onSessionExpired: (code?: string | null) => void
}

let getAccessToken: () => string | null = () => null
let getRefreshToken: () => string | null = () => null
let onTokenRefreshed: (pair: TokenPair) => void = () => {}
let onSessionExpired: (code?: string | null) => void = () => {}

export function configureClient(options: ConfigureOptions): void {
  getAccessToken = options.getAccessToken
  getRefreshToken = options.getRefreshToken
  onTokenRefreshed = options.onTokenRefreshed
  onSessionExpired = options.onSessionExpired
}

export function getApiBaseUrl(): string {
  return window.__CONFIG__?.apiUrl ?? import.meta.env.VITE_API_URL ?? ''
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { code?: string; detail?: string }
    return new ApiError(response.status, body.code ?? null, body.detail ?? response.statusText)
  } catch {
    return new ApiError(response.status, null, response.statusText)
  }
}

/**
 * Refresh-токен одноразовый, и повторное предъявление отзывает **всю цепочку**: бэкенд считает это
 * кражей. Поэтому параллельные 401 обязаны делить одну попытку обновления, а не бежать каждый свою.
 */
let inflightRefresh: Promise<TokenPair> | null = null

async function performRefresh(): Promise<TokenPair> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) throw new ApiError(401, 'REQUIRED_TOKEN', 'Сессия истекла')

  const response = await fetch(`${getApiBaseUrl()}/v0/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
  if (!response.ok) {
    const error = await toApiError(response)
    onSessionExpired(error.code)
    throw error
  }

  const pair = (await response.json()) as TokenPair
  onTokenRefreshed(pair)
  return pair
}

function refreshOnce(): Promise<TokenPair> {
  inflightRefresh ??= performRefresh().finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}

interface RequestOptions {
  method: string
  body?: unknown
  skipAuth?: boolean
  retry?: boolean
}

async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const token = getAccessToken()
  if (!options.skipAuth && token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: options.method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  if (response.status === 401 && !options.skipAuth && options.retry !== false) {
    await refreshOnce()
    return request<T>(path, { ...options, retry: false })
  }
  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

export const http = {
  get: <T>(path: string, options?: { skipAuth?: boolean }) =>
    request<T>(path, { method: 'GET', ...options }),
  post: <T>(path: string, body?: unknown, options?: { skipAuth?: boolean }) =>
    request<T>(path, { method: 'POST', body, ...options }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
